import { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { useToast } from '@/hooks/useKeyboardShortcuts.js';
import exportCsv from '@/utils/exportCsv.js';
import {
  VACACIONES_LIST,
  VACACION_DELETE,
  VACACION_APPROVE,
  VACACION_REJECT,
  VACACION_SET_GOCE,
} from '@/graphql/rrhh/vacaciones.gql.js';

const ESTADOS = [
  { label: 'Todos', value: '' },
  { label: 'Solicitada', value: 'Solicitada' },
  { label: 'Aprobada', value: 'Aprobada' },
  { label: 'Rechazada', value: 'Rechazada' },
  { label: 'Goce', value: 'Goce' },
];

const INITIAL_FILTERS = {
  periodo: '',
  estado: '',
  sucursal: '',
  empleado: '',
};

const INITIAL_PAGINATION = {
  page: 1,
  perPage: 10,
};

const ORDENES = [
  { label: 'Más recientes', value: 'desde:DESC' },
  { label: 'Empleado', value: 'empleadoNombre:ASC' },
  { label: 'Periodo', value: 'periodo:DESC' },
];

function mapFilters(filters) {
  return {
    periodo: filters.periodo || null,
    estado: filters.estado || null,
    sucursalId: filters.sucursal ? Number(filters.sucursal) : null,
    empleadoId: filters.empleado ? Number(filters.empleado) : null,
  };
}

function getEstadoClass(estado) {
  switch (estado) {
    case 'Aprobada':
      return 'bg-[rgba(var(--brand),0.18)] text-[rgb(var(--brand-700))]';
    case 'Solicitada':
      return 'bg-[rgba(var(--muted),0.18)] text-[rgba(var(--muted),0.95)]';
    case 'Goce':
      return 'bg-[rgba(var(--fg),0.08)] text-[rgb(var(--fg))]';
    case 'Rechazada':
      return 'bg-[rgba(var(--fg),0.08)] text-[rgb(var(--fg))]';
    default:
      return 'bg-[rgba(var(--muted),0.15)] text-[rgba(var(--muted),0.95)]';
  }
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ListadoVacaciones() {
  const navigate = useNavigate();
  const toast = useToast();
  const [filterForm, setFilterForm] = useState(INITIAL_FILTERS);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [order, setOrder] = useState({ field: 'desde', direction: 'DESC' });

  const [{ data, fetching, error }] = useQuery({
    query: VACACIONES_LIST,
    variables: {
      filters: mapFilters(filters),
      pagination,
      order,
    },
  });

  const [, deleteVacacion] = useMutation(VACACION_DELETE);
  const [, approveVacacion] = useMutation(VACACION_APPROVE);
  const [, rejectVacacion] = useMutation(VACACION_REJECT);
  const [, goceVacacion] = useMutation(VACACION_SET_GOCE);

  const vacaciones = data?.vacaciones?.items ?? [];
  const pageInfo = data?.vacaciones?.pageInfo ?? { ...pagination, totalItems: vacaciones.length, totalPages: 1 };
  const totalVacaciones = data?.vacaciones?.total ?? vacaciones.length;

  const handleSubmitFilters = (event) => {
    event.preventDefault();
    setFilters(filterForm);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleOrderChange = (event) => {
    const [field, direction] = event.target.value.split(':');
    setOrder({ field, direction });
  };

  const handleExport = () => {
    if (!vacaciones.length) {
      toast.info('No hay vacaciones para exportar con los filtros actuales.');
      return;
    }
    exportCsv({
      filename: 'vacaciones.csv',
      columns: [
        { label: 'ID', key: 'id' },
        { label: 'Empleado', key: 'empleadoNombre' },
        { label: 'Periodo', key: 'periodo' },
        { label: 'Desde', key: 'desde' },
        { label: 'Hasta', key: 'hasta' },
        { label: 'Días tomados', key: 'diasTomados' },
        { label: 'Días restantes', key: 'diasRestantes' },
        { label: 'Estado', key: 'estado' },
        { label: 'Sucursal', key: 'sucursalNombre' },
      ],
      rows: vacaciones,
    });
    toast.success('Exportamos el CSV con las vacaciones listadas.');
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('¿Eliminar la solicitud de vacaciones?');
    if (!confirmed) return;
    const result = await deleteVacacion({ id });
    if (result.error) {
      toast.error(result.error?.message || 'No pudimos eliminar la solicitud.');
      return;
    }
    toast.success('Solicitud eliminada.');
  };

  const handleApprove = async (id) => {
    const result = await approveVacacion({ id });
    if (result.error) {
      toast.error(result.error?.message || 'No pudimos aprobar la solicitud.');
      return;
    }
    toast.success('Vacaciones aprobadas.');
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Motivo de rechazo (opcional):');
    const result = await rejectVacacion({ id, aprobadorId: reason ? 1 : null });
    if (result.error) {
      toast.error(result.error?.message || 'No pudimos rechazar la solicitud.');
      return;
    }
    toast.success('Solicitud rechazada.');
  };

  const handleGoce = async (id) => {
    const result = await goceVacacion({ id });
    if (result.error) {
      toast.error(result.error?.message || 'No pudimos marcar la solicitud en goce.');
      return;
    }
    toast.success('Marcamos la licencia como en goce.');
  };

  const summaryText = useMemo(() => `${totalVacaciones} solicitudes registradas`, [totalVacaciones]);

  const isEmpty = !fetching && vacaciones.length === 0;

  return (
    <div className="w-full">
      <PageHeader
        title="Vacaciones"
        description="Gestioná solicitudes, saldos y aprobaciones de vacaciones."
        breadcrumbs={[
          { label: 'RRHH', href: '/rrhh' },
          { label: 'Vacaciones' },
        ]}
        actions={(
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleExport}
              className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Exportar CSV
            </button>
            <button
              type="button"
              onClick={() => navigate('/rrhh/vacaciones/nueva')}
              className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] hover:bg-[rgb(var(--brand-700))]"
            >
              Nueva solicitud
            </button>
          </div>
        )}
      >
        <p className="text-sm text-[rgba(var(--muted),0.9)]">{summaryText}</p>
      </PageHeader>

      <section className="mx-auto w-full max-w-[1400px]">
        <div className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.95)] p-6 shadow-[0_8px_30px_rgba(var(--shadow),0.15)]">
          <form onSubmit={handleSubmitFilters} className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-3">
              <Field label="Periodo" htmlFor="periodo" hint="Ej: 2024">
                <input
                  id="periodo"
                  value={filterForm.periodo}
                  onChange={event => setFilterForm(prev => ({ ...prev, periodo: event.target.value }))}
                  placeholder="2024"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-3">
              <Field label="Estado" htmlFor="estado">
                <select
                  id="estado"
                  value={filterForm.estado}
                  onChange={event => setFilterForm(prev => ({ ...prev, estado: event.target.value }))}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  {ESTADOS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="col-span-12 md:col-span-3">
              <Field label="Sucursal" htmlFor="sucursal">
                <input
                  id="sucursal"
                  value={filterForm.sucursal}
                  onChange={event => setFilterForm(prev => ({ ...prev, sucursal: event.target.value }))}
                  placeholder="ID sucursal"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-3">
              <Field label="Empleado" htmlFor="empleado" hint="ID colaborador">
                <input
                  id="empleado"
                  value={filterForm.empleado}
                  onChange={event => setFilterForm(prev => ({ ...prev, empleado: event.target.value }))}
                  placeholder="Ej: 100"
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
            <div className="col-span-12 md:col-span-3">
              <Field label="Orden" htmlFor="order">
                <select
                  id="order"
                  value={`${order.field}:${order.direction}`}
                  onChange={handleOrderChange}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  {ORDENES.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="col-span-12 flex flex-wrap items-end justify-between gap-3">
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] hover:bg-[rgb(var(--brand-700))]"
                >
                  Aplicar filtros
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFilterForm(INITIAL_FILTERS);
                    setFilters(INITIAL_FILTERS);
                    setPagination(INITIAL_PAGINATION);
                  }}
                  className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                >
                  Limpiar
                </button>
              </div>
              <p className="text-sm text-[rgba(var(--muted),0.9)]">{totalVacaciones} registros</p>
            </div>
          </form>

          <div className="mt-8 overflow-x-auto">
            <table className="min-w-full table-auto border-separate border-spacing-y-2">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-[rgba(var(--muted),0.9)]">
                  <th className="px-3 py-2">Empleado</th>
                  <th className="px-3 py-2">Periodo</th>
                  <th className="px-3 py-2">Desde</th>
                  <th className="px-3 py-2">Hasta</th>
                  <th className="px-3 py-2">Días</th>
                  <th className="px-3 py-2">Restantes</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {fetching ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-sm text-[rgba(var(--muted),0.9)]">
                      Cargando solicitudes...
                    </td>
                  </tr>
                ) : null}

                {error ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-6 text-center text-sm text-[rgba(var(--muted),0.9)]">
                      No pudimos cargar las vacaciones.
                    </td>
                  </tr>
                ) : null}

                {isEmpty ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-6">
                      <EmptyState
                        title="Sin solicitudes"
                        description="No encontramos vacaciones con los filtros aplicados."
                      />
                    </td>
                  </tr>
                ) : null}

                {vacaciones.map(item => (
                  <tr key={item.id} className="rounded-2xl bg-[rgba(var(--subpanel),0.35)] text-sm text-[rgb(var(--fg))] shadow-[0_4px_16px_rgba(var(--shadow),0.06)]">
                    <td className="rounded-l-2xl px-3 py-3 align-top">
                      <div className="flex flex-col">
                        <span className="font-semibold">{item.empleadoNombre}</span>
                        <span className="text-xs text-[rgba(var(--muted),0.9)]">ID {item.empleadoId}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top">{item.periodo}</td>
                    <td className="px-3 py-3 align-top">{formatDate(item.desde)}</td>
                    <td className="px-3 py-3 align-top">{formatDate(item.hasta)}</td>
                    <td className="px-3 py-3 align-top">{item.diasTomados}</td>
                    <td className="px-3 py-3 align-top">{item.diasRestantes}</td>
                    <td className="px-3 py-3 align-top">
                      <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getEstadoClass(item.estado)}`}>
                        {item.estado}
                      </span>
                    </td>
                    <td className="rounded-r-2xl px-3 py-3 align-top">
                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/rrhh/vacaciones/${item.id}/editar`)}
                          className="rounded-lg border border-[rgba(var(--border),0.4)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--panel),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                        >
                          Editar
                        </button>
                        {item.estado === 'Solicitada' ? (
                          <>
                            <button
                              type="button"
                              onClick={() => handleApprove(item.id)}
                              className="rounded-lg bg-[rgba(var(--brand),0.18)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--brand-700))] transition hover:bg-[rgba(var(--brand),0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                            >
                              Aprobar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleReject(item.id)}
                              className="rounded-lg bg-[rgba(var(--fg),0.08)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                            >
                              Rechazar
                            </button>
                          </>
                        ) : null}
                        {item.estado === 'Aprobada' ? (
                          <button
                            type="button"
                            onClick={() => handleGoce(item.id)}
                            className="rounded-lg bg-[rgba(var(--brand),0.18)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--brand-700))] transition hover:bg-[rgba(var(--brand),0.25)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                          >
                            Marcar en goce
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => handleDelete(item.id)}
                          className="rounded-lg bg-[rgba(var(--fg),0.08)] px-3 py-1.5 text-xs font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-[rgba(var(--muted),0.9)]">
              Mostrando {vacaciones.length} de {totalVacaciones} solicitudes
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, (prev.page || 1) - 1) }))}
                disabled={(pageInfo.page || 1) <= 1}
                className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-1.5 text-sm font-medium text-[rgba(var(--muted),0.95)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Anterior
              </button>
              <span className="text-sm font-medium text-[rgb(var(--fg))]">
                Página {pageInfo.page || 1} de {pageInfo.totalPages || 1}
              </span>
              <button
                type="button"
                onClick={() => setPagination(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
                disabled={(pageInfo.page || 1) >= (pageInfo.totalPages || 1)}
                className="rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.7)] px-3 py-1.5 text-sm font-medium text-[rgba(var(--muted),0.95)] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
